import { useQueueWorkflowStore } from "@elixir/stores";
import { Drawer, notification } from "antd";
import { DrawerHeaderUi, PrimaryButton, TableHeaderUi } from "ui";
import { PlusRegularIcon } from "@elixir/icons";
import { SUBJECT_KEYS, TWorkflowView, WORKFLOW_VIEW_POPUP_STATE } from "@elixir/types";
import ViewAllQueuesTableRowItemUi, { THeaderConfig } from "./table-row";
import useAllQueuesQuery from "../../../apis/tryst/get-all-queues";
import usePatchUpdateWorkflowView from "../../../apis/tryst/workflow-view/use-patch-update-workflow-view";
import useGetQueueConfig from "../../../apis/tryst/get-queue-config";
import { getAclViewClassName } from "@elixir/utils";

const headerConfig: Array<{
  key: string;
  width?: string | number;
  title: string;
  align?: "left" | "right" | "center";
}> = [
    {
      key: "queue_name",
      title: "Queue Name",
      width: "40%",
    },
    {
      key: "patients_in_queue",
      title: "Patients in Queue",
      width: "20%",
    },
    {
      key: "created_at",
      title: "Created At",
      width: "20%",
    },
    // {
    //   key: "archive",
    //   title: "Inactive/active",
    //   width: "20%",
    //   align: "right",
    // },
    {
      key: "edit",
      title: "Actions",
      width: "20%",
      align: "right",
    },
  ];

export const getSettingsBodyConfig = (headerConfig: THeaderConfig) => {
  return headerConfig.reduce(
    (curr, val) => {
      return {
        ...curr,
        [val.key]: {
          width: val.width,
          align: val.align,
        },
      };
    },
    {} as Record<
      string,
      {
        width?: string | number;
        align?: "left" | "right" | "center";
      }
    >
  );
};

const bodyConfig = getSettingsBodyConfig(headerConfig);

const ViewAllQueues = () => {
  const setPopupState = useQueueWorkflowStore(
    (state) => state.setCreateViewPopupState
  );
  const setViewData = useQueueWorkflowStore((state) => state.setViewData);
  const clearSelectedPopupData = useQueueWorkflowStore(
    (state) => state.clearSelectedPopupData
  );
  const { data: allQueuesData } = useAllQueuesQuery();

  const { data: queueConfigData } = useGetQueueConfig();
  const { workflow_views = [] } = queueConfigData?.data || {};

  const { mutateAsync: patchUpdateWorkflowView } = usePatchUpdateWorkflowView();

  const handleDeleteView = async ({
    viewId,
    name,
  }: {
    viewId: string;
    name: string;
  }) => {
    const { success, error } = await patchUpdateWorkflowView({
      view_id: viewId,
      data: { archive: true, name: name },
    });
    if (success) {
      notification.success({
        message: "View deleted successfully!",
        placement: "bottomRight",
      });
    } else {
      notification.error({
        message: error || "Error occurred while deleting view!",
        placement: "bottomRight",
      });
    }
    setViewData(undefined);
    clearSelectedPopupData();
  };

  const handleEditView = (viewData: TWorkflowView) => {
    setViewData(viewData);
    setPopupState(WORKFLOW_VIEW_POPUP_STATE.EDIT_FILTERS);
  };

  return (
    <Drawer
      bodyStyle={{ padding: 0 }}
      closable={false}
      placement={"right"}
      onClose={() => {
        setPopupState(undefined);
        setViewData(undefined);
        clearSelectedPopupData();
      }}
      open={true}
      width={880}
      extra={null}
      destroyOnClose={true}
    >
      <DrawerHeaderUi
        title="Edit queue"
        onClose={() => {
          setPopupState(undefined);
          setViewData(undefined);
          clearSelectedPopupData();
        }}
        headerRightComponent={
          <div className={`dr-flex dr-flex-row dr-gap-x-16 ${getAclViewClassName(SUBJECT_KEYS.CREATE_CUSTOM_QUEUE)}`}>
            <PrimaryButton
              title="Add queue"
              onClick={() =>
                setPopupState(WORKFLOW_VIEW_POPUP_STATE.VIEW_NAMING)
              }
              buttonIconPosition="left"
              IconComponent={() => (
                <PlusRegularIcon className="dr-w-12 dr-h-14 dr-text-doctor-darwin-neutral-0" />
              )}
            />
          </div>
        }
        extraClassname="dr-border-b dr-border-darwin-neutral-200"
      />
      <div className="dr-h-full dr-bg-darwin-touch-neutral-50">
        <div className="dr-px-16 dr-pt-16">
          <div className="dr-flex dr-flex-col dr-w-full dr-border dr-border-darwin-neutral-200 dr-rounded-8 dr-overflow-hidden dr-h-[calc(100%-76px)] dr-bg-darwin-touch-neutral-0">
            <TableHeaderUi headerConfig={headerConfig} />
            <div className="dr-flex-1 dr-w-full">
              {workflow_views?.map((view, index) => (
                <ViewAllQueuesTableRowItemUi
                  key={view.viewId}
                  rowConfig={bodyConfig}
                  viewItem={{
                    ...view,
                    patient_count: allQueuesData?.[view.viewId!]?.length ?? 0,
                  }}
                  isLastItem={index === (workflow_views?.length || 0) - 1}
                  onEditClick={() => handleEditView(view)}
                  onDeleteClick={() =>
                    handleDeleteView({
                      viewId: view.viewId!,
                      name: view.name,
                    })
                  }
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default ViewAllQueues;
